import React, { useState, useRef, useEffect } from 'react';
import { TagChip } from '@/shared/components/ui';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatLeadCoupon } from '@troia-v3/types';
import { ptBR } from 'date-fns/locale';
import { PopulatedLead } from '../types';
import { leadsService } from '../services/leadsService';
import { useTransferLead } from '../hooks/useLeadMutations';
import { Drawer } from '@/shared/components/ui/Drawer';
import { useLeadActivities, useAddLeadActivity } from '../hooks/useLeadActivities';
import { useContact, useUpdateContact } from '@/modules/chat/hooks/useContacts';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useUnitsDropdown } from '@/shared/hooks';
import { LeadTimeline } from './LeadTimeline';
import { AddActivityForm } from './AddActivityForm';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import { StartConversationDialog } from '@/modules/chat/components/StartConversationDialog';
import EditContactDialog, { type EditContactFormData } from '@/modules/chat/components/EditContactDialog';
import { TransferLeadDialog } from './TransferLeadDialog';
import { WinLeadDialog, type WinLeadData } from './WinLeadDialog';
import { LoseLeadDialog, type LoseLeadData } from './LoseLeadDialog';
import TagsDialog from '@/modules/chat/components/TagsDialog';
import { LeadInterestsTab } from './LeadInterestsTab';
import { formatPhoneNumber } from '@/shared/utils';
import { Icon } from '@/shared/icons';
import { CardActionsMenu, DrawerHeaderButton, DrawerTabBar, type CardAction } from '@/shared/components/ui';
import { useIsMobile } from '@/shared/hooks';
import { ChecklistEntityTab } from '@/modules/checklists/components/ChecklistEntityTab';
type TabType = 'activities' | 'details' | 'interests' | 'checklists';

/** `.detail-card` do mockup drwDetail: bg #f9fafb, borda #eef1f5, radius 10, h4 uppercase. */
const DetailCard: React.FC<{ title?: string; action?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties }> = ({ title, action, children, style }) => (
  <div
    className="rounded-[10px]"
    style={{ backgroundColor: '#f9fafb', border: '1px solid #eef1f5', padding: '13px 14px', marginBottom: 12, ...style }}
  >
    {title && (
      <div className="flex items-center justify-between" style={{ marginBottom: 9 }}>
        <h4 className="text-xs font-bold uppercase" style={{ letterSpacing: '.04em', color: '#94a3b8', margin: 0 }}>
          {title}
        </h4>
        {action}
      </div>
    )}
    {children}
  </div>
);

/** `.kv` do mockup: linha chave (muted) → valor (direita, medium). */
const Kv: React.FC<{ k: string; children: React.ReactNode; vStyle?: React.CSSProperties }> = ({ k, children, vStyle }) => (
  <div className="flex justify-between gap-2.5 text-[13px]" style={{ padding: '4px 0' }}>
    <span style={{ color: '#64748b' }}>{k}</span>
    <span className="text-right min-w-0" style={{ color: '#1e293b', fontWeight: 500, ...vStyle }}>{children}</span>
  </div>
);

/**
 * Props do LeadDetailsDialog
 */
export interface LeadDetailsDialogProps {
  /** Se o drawer está aberto */
  isOpen: boolean;
  /** Callback para fechar o drawer */
  onClose: () => void;
  /** Lead a ser exibido */
  lead: PopulatedLead | null;
  /** Callback executado quando atividade é adicionada com sucesso (para refetch) */
  onSuccess?: () => void;
}

/**
 * Drawer de Detalhes do Lead (tamanho lg)
 *
 * Always-light theme para exibir todos os detalhes de um lead.
 * Organizado em tabs: Detalhes e Atividades.
 * Tab Atividades: Timeline + Formulário de nova atividade
 * Tab Detalhes: Informações completas do lead
 *
 * @example
 * ```tsx
 * <LeadDetailsDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   lead={selectedLead}
 * />
 * ```
 */
export const LeadDetailsDialog: React.FC<LeadDetailsDialogProps> = ({
  isOpen,
  onClose,
  lead,
  onSuccess
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('activities');
  const isMobile = useIsMobile();
  const [showStartConversation, setShowStartConversation] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showWinDialog, setShowWinDialog] = useState(false);
  const [showLoseDialog, setShowLoseDialog] = useState(false);
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);

  // Ao fechar o drawer pai, fecha qualquer diálogo-filho aberto — senão o estado
  // persiste e o filho reabre "aberto" na próxima vez que o pai abre. Ver DS §Overlays
  // (ciclo pai→filho). Regra: fechar um drawer/modal que chamou outro fecha o filho junto.
  useEffect(() => {
    if (!isOpen) {
      setShowStartConversation(false);
      setShowEditContact(false);
      setShowTransferDialog(false);
      setShowWinDialog(false);
      setShowLoseDialog(false);
      setIsTagsDialogOpen(false);
    }
  }, [isOpen]);

  // Manter referência do último lead válido para exibir durante animação de fechamento
  const lastLeadRef = useRef<PopulatedLead | null>(null);
  if (lead) lastLeadRef.current = lead;
  const displayLead = lead || lastLeadRef.current;

  const { data: activitiesData, isLoading: activitiesLoading } = useLeadActivities(
    lead?.id || null
  );

  // Buscar contato completo quando abrir a modal de edição
  const { data: fullContact } = useContact(showEditContact ? lead?.contactId : undefined);

  // Bloco "Captura por QR Code" (aba Detalhes): `lead.capture` só guarda ids —
  // resolve nomes de vendedor/equipe/unidade via dropdowns cross-module, só
  // quando a aba Detalhes está aberta e o lead tem captura (evita N+1 fetch).
  const captureLookupsEnabled = isOpen && activeTab === 'details' && Boolean(displayLead?.capture);
  const { data: captureUsers } = useUsersDropdown({ enabled: captureLookupsEnabled });
  const { data: captureTeams } = useTeamsDropdown({ enabled: captureLookupsEnabled });
  const { data: captureUnits } = useUnitsDropdown(captureLookupsEnabled);
  const captureUser = displayLead?.capture
    ? captureUsers?.find((u) => u.id === displayLead.capture?.capturedBy)
    : undefined;
  const captureCapturedByName = captureUser?.name ?? displayLead?.assignee?.name ?? displayLead?.capture?.capturedBy;
  const captureTeamName = displayLead?.capture?.teamId
    ? captureTeams?.find((t) => t.id === displayLead.capture?.teamId)?.name ?? displayLead.capture.teamId
    : undefined;
  const captureUnitName = displayLead?.capture?.unitId
    ? captureUnits?.find((u) => u.value === displayLead.capture?.unitId)?.label ?? displayLead.capture.unitId
    : undefined;
  /** "Equipe X · Unidade Y" numa linha só (mockup 13-B linha 44) — só o que existir; nada se nenhum. */
  const captureTeamUnitLabel = [captureTeamName, captureUnitName].filter(Boolean).join(' · ') || undefined;
  /** "Offline · qrcode" (mockup linha 47) — de `lead.source`/`lead.origin` reais
   *  (capture-service.ts grava sempre `source:'offline'`, `origin:'qrcode'`),
   *  capitalizando só o `source` como no mockup. */
  const captureOriginLabel = displayLead?.source
    ? `${displayLead.source.charAt(0).toUpperCase()}${displayLead.source.slice(1)}${displayLead.origin ? ` · ${displayLead.origin}` : ''}`
    : displayLead?.origin;

  const queryClient = useQueryClient();
  const addActivityMutation = useAddLeadActivity(lead?.id || '');
  const updateContactMutation = useUpdateContact();
  const transferLeadMutation = useTransferLead();

  const handleAddActivity = async (data: any) => {
    await addActivityMutation.mutateAsync(data);
    onSuccess?.();
  };

  // Handler para transferir lead
  const handleTransferLead = async (
    transferType: 'assignee' | 'funnel',
    data: {
      newAssigneeId?: string;
      newFunnelId?: string;
      newStepId?: string;
    }
  ) => {
    if (!lead) return;

    try {
      if (transferType === 'assignee') {
        await transferLeadMutation.mutateAsync({
          leadId: lead.id,
          body: { toUserId: data.newAssigneeId ?? null },
        });
      } else {
        await leadsService.updateLead(lead.id, {
          funnelId: data.newFunnelId!,
          stepId: data.newStepId!,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['leads'] });

      setShowTransferDialog(false);
      onSuccess?.();
      toast.success('Lead transferido com sucesso!');
    } catch (error) {
      console.error('Erro ao transferir lead:', error);
      toast.error('Erro ao transferir lead. Tente novamente.');
      throw error;
    }
  };

  // Handler para marcar lead como ganho
  const handleWinLead = async (data: WinLeadData) => {
    if (!lead) return;

    try {
      await leadsService.updateLead(lead.id, data);

      await queryClient.invalidateQueries({ queryKey: ['leads'] });

      setShowWinDialog(false);
      onSuccess?.();
      alert('Lead marcado como ganho com sucesso!');
    } catch (error) {
      console.error('Erro ao marcar lead como ganho:', error);
      alert('Erro ao marcar lead como ganho. Tente novamente.');
    }
  };

  // Handler para marcar lead como perdido
  const handleLoseLead = async (data: LoseLeadData) => {
    if (!lead) return;

    try {
      await leadsService.updateLead(lead.id, data);

      await queryClient.invalidateQueries({ queryKey: ['leads'] });

      setShowLoseDialog(false);
      onSuccess?.();
      alert('Lead marcado como perdido com sucesso!');
    } catch (error) {
      console.error('Erro ao marcar lead como perdido:', error);
      alert('Erro ao marcar lead como perdido. Tente novamente.');
    }
  };

  // Handler para atualizar contato
  const handleUpdateContact = async (contactId: string, data: EditContactFormData, avatar?: File) => {
    if (!lead?.contact) return;

    try {
      await updateContactMutation.mutateAsync({ id: contactId, data, avatar });

      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      await queryClient.invalidateQueries({ queryKey: ['leads'] });

      setShowEditContact(false);
      onSuccess?.();
      alert('Contato atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar contato:', error);
      alert('Erro ao atualizar contato. Tente novamente.');
    }
  };

  // Handler para retomar negociação
  const handleResumeNegotiation = async () => {
    if (!lead) return;

    try {
      await leadsService.updateLead(lead.id, {
        businessStatus: 'pending'
      });

      await queryClient.invalidateQueries({ queryKey: ['leads'] });

      onSuccess?.();
      toast.success('Negociação retomada com sucesso!');
    } catch (error) {
      console.error('Erro ao retomar negociação:', error);
      toast.error('Erro ao retomar negociação. Tente novamente.');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getPriorityLabel = (priority: string) => {
    const labels: Record<string, string> = {
      urgent: 'Urgente',
      high: 'Alta',
      medium: 'Média',
      low: 'Baixa'
    };
    return labels[priority] || priority;
  };

  const getBusinessStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Pendente',
      won: 'Ganho',
      lost: 'Perdido'
    };
    return labels[status] || status;
  };

  const getQualifyStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Pendente',
      qualified: 'Qualificado',
      disqualified: 'Desqualificado'
    };
    return labels[status] || status;
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        customHeader={displayLead ? (
          <>
            {/* `.drw-head` do mockup: drw-av 40 slate + título/sub + drw-hicons + X */}
            <div
              className="flex items-center gap-3 border-b"
              style={{ padding: '15px 18px', borderColor: '#eef1f5' }}
            >
              <div
                className="relative flex items-center justify-center text-white font-semibold flex-none overflow-hidden"
                style={{ width: 40, height: 40, borderRadius: '32%', backgroundColor: '#64748b' }}
              >
                {(displayLead.contact?.name || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                {displayLead.contact?.picture && (
                  <img
                    src={displayLead.contact.picture}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-[16px] font-bold whitespace-nowrap truncate" style={{ color: '#1e293b' }}>
                  {displayLead.contact?.name || 'Sem nome'}
                </h2>
                <p className="text-xs" style={{ color: '#64748b' }}>Detalhes do Lead</p>
              </div>
              <div className="flex items-center gap-[5px] ml-auto flex-nowrap">
                {isMobile ? (
                  /* Mobile (DS 2026-08-11): TODAS as ações agrupadas num único
                     kebab (•••) ao lado do X — nunca fileira de icon-buttons. */
                  <CardActionsMenu
                    actions={[
                      ...(displayLead.businessStatus !== 'won' && displayLead.businessStatus !== 'lost'
                        ? ([
                            { label: 'Ganho', icon: 'trending-up', onClick: () => setShowWinDialog(true) },
                            { label: 'Perdido', icon: 'alert-circle', onClick: () => setShowLoseDialog(true) },
                          ] satisfies CardAction[])
                        : []),
                      { label: 'Transferir', icon: 'user-plus', onClick: () => setShowTransferDialog(true) },
                      { label: 'Iniciar atendimento', icon: 'message-square', onClick: () => setShowStartConversation(true) },
                    ]}
                    data-testid="lead-details-more-menu-button"
                  />
                ) : (
                  <>
                    {displayLead.businessStatus !== 'won' && displayLead.businessStatus !== 'lost' && (
                      <>
                        <DrawerHeaderButton icon="trending-up" label="Ganho" onClick={() => setShowWinDialog(true)} data-testid="lead-win-button" />
                        <DrawerHeaderButton icon="alert-circle" label="Perdido" onClick={() => setShowLoseDialog(true)} data-testid="lead-lose-button" />
                      </>
                    )}
                    <DrawerHeaderButton icon="user-plus" label="Transferir" onClick={() => setShowTransferDialog(true)} data-testid="lead-transfer-button" />
                    <DrawerHeaderButton icon="message-square" label="Iniciar atendimento" prim onClick={() => setShowStartConversation(true)} data-testid="lead-start-conversation" />
                  </>
                )}
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors flex-none"
                  style={{ color: '#64748b' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid="lead-details-close"
                >
                  <Icon name="x" className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Abas — DrawerTabBar shared (`.tabs2`, padrão do DS 2026-08-11):
                largura do conteúdo, Detalhes primeiro. */}
            <DrawerTabBar
              items={[
                { id: 'details', label: 'Detalhes', icon: 'user', testid: 'lead-details-tab-details' },
                { id: 'activities', label: 'Atividades', icon: 'activity', testid: 'lead-details-tab-activities' },
                { id: 'interests', label: 'Interesses', icon: 'star', testid: 'lead-details-tab-interests' },
                { id: 'checklists', label: 'Checklists/Formulários', icon: 'check-square', testid: 'lead-details-tab-checklists' },
              ]}
              active={activeTab}
              onChange={(id) => setActiveTab(id as TabType)}
            />
          </>
        ) : undefined}
      >
        {/* `.drw-body` do mockup: padding 16px 18px */}
        <div style={{ padding: '16px 18px' }}>
        {displayLead && (
          <>
            {/* Tab: Atividades (mockup: detail-card do form + sec-h + timeline sem card) */}
            {activeTab === 'activities' && (
              <div>
                <DetailCard title="Nova atividade">
                  <AddActivityForm
                    onSubmit={handleAddActivity}
                    loading={addActivityMutation.isPending}
                  />
                </DetailCard>

                <div
                  className="text-xs font-bold uppercase"
                  style={{ letterSpacing: '.04em', color: '#94a3b8', margin: '16px 0 12px' }}
                >
                  Histórico de Atividades
                </div>
                <LeadTimeline
                  activities={activitiesData?.activities || []}
                  loading={activitiesLoading}
                  onActivityComplete={onSuccess}
                />
              </div>
            )}

            {/* Tab: Detalhes (mockup: detail-cards com linhas .kv) */}
            {activeTab === 'details' && (
              <div>
                <DetailCard title="Informações do Lead">
                  {displayLead.budget != null && displayLead.budget > 0 && (
                    <Kv k="Valor Estimado" vStyle={{ color: '#059669', fontWeight: 700 }}>
                      {formatCurrency(displayLead.budget)}
                    </Kv>
                  )}
                  {displayLead.segment && <Kv k="Segmento">{displayLead.segment}</Kv>}
                  {displayLead.description && (
                    <div className="text-[13px] whitespace-pre-line" style={{ color: '#475569', marginTop: 8 }}>
                      {displayLead.description.replace(/<br\s*\/?>/gi, '\n')}
                    </div>
                  )}
                </DetailCard>

                <DetailCard
                  title="Informações de Contato"
                  action={
                    <button
                      onClick={() => setShowEditContact(true)}
                      className="inline-flex items-center gap-[5px] text-xs font-semibold transition-opacity hover:opacity-80"
                      style={{ color: '#7c3aed' }}
                      data-testid="lead-edit-contact"
                    >
                      <Icon name="edit" size={12} />
                      Editar
                    </button>
                  }
                >
                  <Kv k={(displayLead.contact?.identifiers?.email?.length ?? 0) > 1 ? 'E-mails' : 'E-mail'}>
                    {(displayLead.contact?.identifiers?.email?.length ?? 0) > 0
                      ? displayLead.contact?.identifiers?.email?.map((email, i) => (
                          <span key={i} className="block truncate"><PrivacyText>{email}</PrivacyText></span>
                        ))
                      : '—'}
                  </Kv>
                  <Kv k={(displayLead.contact?.identifiers?.phone?.length ?? 0) > 1 ? 'Telefones' : 'Telefone'}>
                    {(displayLead.contact?.identifiers?.phone?.length ?? 0) > 0
                      ? displayLead.contact?.identifiers?.phone?.map((phone, i) => (
                          <span key={i} className="block truncate"><PrivacyText>{formatPhoneNumber(phone)}</PrivacyText></span>
                        ))
                      : '—'}
                  </Kv>
                </DetailCard>

                {/* Tags do contato — recurso real sem slot no mockup; card na mesma gramática */}
                <DetailCard
                  title="Tags do Contato"
                  action={
                    displayLead.contact?.id ? (
                      <button
                        onClick={() => setIsTagsDialogOpen(true)}
                        className="inline-flex items-center gap-[5px] text-xs font-semibold transition-opacity hover:opacity-80"
                        style={{ color: '#7c3aed' }}
                        data-testid="lead-manage-tags"
                      >
                        <Icon name="tag" size={12} />
                        Gerenciar
                      </button>
                    ) : undefined
                  }
                >
                  <div className="flex flex-wrap gap-2" style={{ marginTop: 2 }}>
                    {displayLead.contact?.tags && displayLead.contact.tags.length > 0 ? (
                      displayLead.contact.tags.map((tag, index) => (
                        <TagChip
                          key={index}
                          name={tag}
                          className="text-xs font-semibold"
                          style={{ padding: '2px 10px' }}
                        />
                      ))
                    ) : (
                      <span className="text-[13px]" style={{ color: '#64748b' }}>Sem tags</span>
                    )}
                  </div>
                </DetailCard>

                <DetailCard
                  title="Status e Prioridade"
                  action={
                    (displayLead.businessStatus === 'won' || displayLead.businessStatus === 'lost') ? (
                      <button
                        onClick={handleResumeNegotiation}
                        className="inline-flex items-center gap-[5px] text-xs font-semibold transition-opacity hover:opacity-80"
                        style={{ color: '#7c3aed' }}
                        data-testid="lead-resume-negotiation"
                      >
                        <Icon name="refresh" size={12} />
                        Retomar negociação
                      </button>
                    ) : undefined
                  }
                >
                  {/* `.badge2` do mockup: pills 12px semibold */}
                  <div className="flex flex-wrap gap-2" style={{ marginTop: 2 }}>
                    <span
                      className="inline-block text-xs font-semibold rounded-full"
                      style={{
                        padding: '2px 10px',
                        backgroundColor: displayLead.priority === 'urgent' ? '#fee2e2' :
                                       displayLead.priority === 'high' ? '#fef3c7' :
                                       displayLead.priority === 'medium' ? '#dbeafe' : '#d1fae5',
                        color: displayLead.priority === 'urgent' ? '#991b1b' :
                              displayLead.priority === 'high' ? '#b45309' :
                              displayLead.priority === 'medium' ? '#1d4ed8' : '#065f46'
                      }}
                    >
                      {getPriorityLabel(displayLead.priority)}
                    </span>
                    <span
                      className="inline-block text-xs font-semibold rounded-full"
                      style={{
                        padding: '2px 10px',
                        backgroundColor: displayLead.businessStatus === 'won' ? '#dcfce7' :
                                       displayLead.businessStatus === 'lost' ? '#fee2e2' : '#f3e8ff',
                        color: displayLead.businessStatus === 'won' ? '#15803d' :
                              displayLead.businessStatus === 'lost' ? '#991b1b' : '#7c3aed'
                      }}
                    >
                      {getBusinessStatusLabel(displayLead.businessStatus || 'pending')}
                    </span>
                    <span
                      className="inline-block text-xs font-semibold rounded-full"
                      style={{
                        padding: '2px 10px',
                        backgroundColor: displayLead.qualifyStatus === 'qualified' ? '#dbeafe' :
                                       displayLead.qualifyStatus === 'disqualified' ? '#fee2e2' : '#f1f5f9',
                        color: displayLead.qualifyStatus === 'qualified' ? '#1d4ed8' :
                              displayLead.qualifyStatus === 'disqualified' ? '#991b1b' : '#475569'
                      }}
                    >
                      {getQualifyStatusLabel(displayLead.qualifyStatus || 'pending')}
                    </span>
                  </div>
                </DetailCard>

                {/* Captura por QR Code (spec §4.8 "Lead › detalhes", mockup 13-B linhas 41-49) — só quando o lead nasceu pela captura */}
                {displayLead.capture && (
                  <DetailCard title="Captura por QR Code" style={{ borderColor: '#e9d5ff' }}>
                    <div data-testid="lead-capture-block">
                      <Kv k="Vendedor" vStyle={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        {captureUser?.avatar ? (
                          <img
                            src={captureUser.avatar}
                            alt=""
                            className="object-cover flex-none"
                            style={{ width: 24, height: 24, borderRadius: '32%' }}
                          />
                        ) : (
                          <span
                            className="flex items-center justify-center text-[10px] font-semibold text-white flex-none"
                            style={{ width: 24, height: 24, borderRadius: '32%', backgroundColor: '#64748b' }}
                          >
                            {(captureCapturedByName || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                          </span>
                        )}
                        <span className="truncate">{captureCapturedByName}</span>
                      </Kv>
                      {captureTeamUnitLabel && <Kv k="Equipe · Unidade">{captureTeamUnitLabel}</Kv>}
                      <Kv k="Captado em">{formatDate(displayLead.capture.filledAt)}</Kv>
                      <Kv k="Confirmado no WhatsApp" vStyle={{ color: displayLead.capture.confirmedAt ? '#059669' : '#94a3b8' }}>
                        {displayLead.capture.confirmedAt
                          ? `${formatDate(displayLead.capture.confirmedAt)} · código ${displayLead.capture.code}`
                          : 'Aguardando WhatsApp'}
                      </Kv>
                      {captureOriginLabel && <Kv k="Origem">{captureOriginLabel}</Kv>}
                    </div>
                  </DetailCard>
                )}

                {/* Cupons de sorteio (2026-09-29) — só leitura: quem dá o número é o sistema (página de captura / automação) */}
                {displayLead.coupons && displayLead.coupons.length > 0 && (
                  <DetailCard title="Cupons do sorteio">
                    <div className="flex flex-wrap gap-1.5" data-testid="lead-coupons">
                      {displayLead.coupons.map((cupom) => (
                        <span
                          key={cupom}
                          className="inline-flex items-center gap-1 text-xs font-semibold rounded-full tabular-nums"
                          style={{ padding: '2px 10px', backgroundColor: '#fef3c7', color: '#92400e' }}
                          data-testid={`lead-coupon-${cupom}`}
                        >
                          🎟️ {formatLeadCoupon(cupom)}
                        </span>
                      ))}
                    </div>
                  </DetailCard>
                )}

                {displayLead.assignee && (
                  <DetailCard title="Responsável">
                    <div className="flex items-center gap-2.5">
                      {displayLead.assignee.picture ? (
                        <img
                          src={displayLead.assignee.picture}
                          alt={displayLead.assignee.name || 'Responsável'}
                          className="object-cover"
                          style={{ width: 30, height: 30, borderRadius: '32%' }}
                        />
                      ) : (
                        <span
                          className="flex items-center justify-center text-[11px] font-semibold text-white"
                          style={{ width: 30, height: 30, borderRadius: '32%', backgroundColor: '#64748b' }}
                        >
                          {displayLead.assignee.name?.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?'}
                        </span>
                      )}
                      <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                        {displayLead.assignee.name || 'Sem nome'}
                      </span>
                    </div>
                  </DetailCard>
                )}

                <DetailCard title="Datas" style={{ marginBottom: 0 }}>
                  <Kv k="Criado em">{formatDate(displayLead.createdAt)}</Kv>
                  {displayLead.lastInteractionAt && (
                    <Kv k="Última interação">{formatDate(displayLead.lastInteractionAt)}</Kv>
                  )}
                  {displayLead.wonDate && (
                    <Kv k="Data da vitória" vStyle={{ color: '#059669' }}>{formatDate(displayLead.wonDate)}</Kv>
                  )}
                  {displayLead.lostDate && (
                    <Kv k="Data da perda" vStyle={{ color: '#dc2626' }}>{formatDate(displayLead.lostDate)}</Kv>
                  )}
                </DetailCard>

                {/* Motivo da perda — estado real sem slot no mockup (anotado) */}
                {displayLead.businessStatus === 'lost' && displayLead.lostReason && (
                  <DetailCard title="Motivo da Perda" style={{ marginTop: 12, marginBottom: 0, backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                    <p className="text-[13px]" style={{ color: '#991b1b' }}>
                      {displayLead.lostReason}
                    </p>
                  </DetailCard>
                )}
              </div>
            )}

            {/* Tab: Interesses */}
            {activeTab === 'interests' && (
              <LeadInterestsTab leadId={displayLead.id} lead={displayLead} onSuccess={onSuccess} />
            )}

            {/* Tab: Checklists (Task 17) — componente ÚNICO reusado no drawer de ticket */}
            {activeTab === 'checklists' && (
              <ChecklistEntityTab entityType="lead" entityId={displayLead.id} isOpen={isOpen} />
            )}
          </>
        )}
        </div>
      </Drawer>

      {/* Tags Dialog */}
      {lead?.contact?.id && (
        <TagsDialog
          contactId={lead.contact.id}
          contactName={lead.contact.name}
          isOpen={isTagsDialogOpen}
          onClose={() => setIsTagsDialogOpen(false)}
        />
      )}

      {/* Start Conversation Dialog — self-portala (StartConversationDialog usa createPortal) */}
      {showStartConversation && lead && (
        <StartConversationDialog
          open={showStartConversation}
          onOpenChange={setShowStartConversation}
          contact={{
            id: lead.contactId,
            name: lead.contact?.name || 'Sem nome',
            identifiers: lead.contact?.identifiers,
          }}
        />
      )}

      {/* Edit Contact Dialog */}
      <EditContactDialog
        isOpen={showEditContact && !!fullContact}
        onClose={() => setShowEditContact(false)}
        onSave={handleUpdateContact}
        contact={fullContact ?? null}
        isLoading={updateContactMutation.isPending}
      />

      {/* Transfer/Win/Lose — cada diálogo self-portala (usa createPortal internamente) */}
      {showTransferDialog && lead && (
        <TransferLeadDialog
          lead={lead}
          onClose={() => setShowTransferDialog(false)}
          onTransfer={handleTransferLead}
        />
      )}

      {showWinDialog && lead && (
        <WinLeadDialog
          isOpen={showWinDialog}
          onClose={() => setShowWinDialog(false)}
          lead={lead}
          onConfirm={handleWinLead}
        />
      )}

      {showLoseDialog && lead && (
        <LoseLeadDialog
          isOpen={showLoseDialog}
          onClose={() => setShowLoseDialog(false)}
          lead={lead}
          onConfirm={handleLoseLead}
        />
      )}
    </>
  );
};
